import { ReactNode } from "react";

import { ScrollArea, ScrollBar } from "../ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "../ui/tooltip";

import {
  TabItemDirectionProps,
  TabItemProps,
  TabTypes
} from "./tab-component.type";

import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";

export const TriggerButtonStyle = cn(
  "aero:flex-1 aero:h-8 aero:rounded-lg aero:gap-1",
  "aero:px-4 aero:py-2 aero:text-sm aero:font-medium",
  "aero:inline-flex aero:items-center aero:justify-center aero:whitespace-nowrap",
  "aero:ring-offset-background aero:transition-all",
  "aero:focus-visible:outline-none aero:focus-visible:ring-2 aero:focus-visible:ring-ring aero:focus-visible:ring-offset-2",
  "aero:disabled:pointer-events-none aero:disabled:opacity-50"
);

export const ActiveStyle = cn(
  "aero:bg-brand-primary-50 aero:border aero:border-brand-primary-700 aero:text-brand-primary-700"
);

export const InactiveStyle = cn(
  "aero:bg-transparent aero:border aero:border-transparent aero:text-neutral-600"
);

function TabsComponent<T extends TabItemProps | TabItemDirectionProps>(
  props: TabTypes<T> & { swapIconPosition?: boolean }
): JSX.Element {
  const {
    tabs,
    activeTab,
    addOnsButtons = [],
    addOnsButton,
    setActiveTab,
    tabClassName,
    tabListClassName,
    scrollable = true,
    swapIconPosition = false
  } = props;

  const hasDirection = tabs.every((tab) => "direction" in tab);
  const currentActiveContent =
    !hasDirection &&
    (tabs.find((f) => f.value === activeTab)?.content as ReactNode);

  const renderLabelIcon = (tab: TabItemProps | TabItemDirectionProps) => {
    if (tab.icon) {
      return swapIconPosition ? (
        <>
          <span className="aero:mr-2">{tab.label}</span>
          {tab.icon}
        </>
      ) : (
        <>
          {tab.icon}
          <span className="aero:ml-2">{tab.label}</span>
        </>
      );
    }

    return tab.label;
  };

  const RenderLabel = ({
    tab,
    tabListClassName
  }: {
    tab: TabItemProps | TabItemDirectionProps;
    tabListClassName?: string;
  }) => {
    if ("direction" in tab) {
      const normalizedActiveTab = activeTab?.replace(/^\//, "") || "";
      const normalizedDirection = tab.direction.replace(/^\//, "");

      const active =
        normalizedActiveTab.length > 0 &&
        normalizedActiveTab.startsWith(normalizedDirection);

      const isDisabled = tab.disabled === true;

      if (isDisabled) {
        return (
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    TriggerButtonStyle,
                    "aero:opacity-50 aero:cursor-not-allowed aero:text-neutral-400",
                    tabListClassName
                  )}
                >
                  {renderLabelIcon(tab)}
                </span>
              </TooltipTrigger>
              {tab.tooltip && (
                <TooltipContent
                  side="bottom"
                  className="aero:bg-neutral-800 aero:text-white aero:text-xs aero:px-3 aero:py-2 aero:max-w-[250px]"
                >
                  {tab.tooltip}
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        );
      }

      return (
        <Link
          to={`${tab.direction}`}
          className={cn(
            TriggerButtonStyle,
            active ? ActiveStyle : InactiveStyle
          )}
        >
          {renderLabelIcon(tab)}
        </Link>
      );
    }

    return (
      <TabsTrigger
        key={tab.value}
        value={tab.value}
        className={cn(
          TriggerButtonStyle,
          "aero:data-[state=active]:bg-brand-primary-50 aero:data-[state=active]:border aero:data-[state=active]:border-brand-primary-700 aero:data-[state=active]:text-brand-primary-700",
          "aero:data-[state=inactive]:bg-transparent aero:data-[state=inactive]:border-transparent aero:data-[state=inactive]:text-neutral-600"
        )}
      >
        {renderLabelIcon(tab)}
      </TabsTrigger>
    );
  };

  const tabContent = () => (
    <TabsContent value={activeTab} className="aero:mt-3">
      {currentActiveContent}
    </TabsContent>
  );

  return (
    <Tabs
      value={activeTab}
      onValueChange={setActiveTab}
      className={cn("aero:w-full", tabClassName)}
    >
      <TabsList
        className={cn(
          "aero:w-full aero:h-auto aero:min-h-12 aero:p-2 aero:gap-0",
          "aero:bg-white aero:rounded-lg aero:border aero:border-neutral-300",
          "aero:shadow-[0px_4px_4px_0px_rgba(127,127,127,0.1)]",
          "aero:flex aero:items-center aero:justify-start",
          tabListClassName
        )}
      >
        <div className="aero:flex aero:items-center aero:gap-0 aero:flex-1 aero:min-w-0 aero:overflow-x-auto hide-scrollbar">
          {tabs.map((tab: TabItemProps | TabItemDirectionProps) => (
            <RenderLabel key={tab.label} tab={tab} />
          ))}
        </div>

        {(addOnsButtons || addOnsButton) && (
          <div className="aero:ml-auto aero:flex aero:items-center aero:space-x-2">
            {addOnsButtons &&
              addOnsButtons.length > 0 &&
              addOnsButtons.map(({ component }, index) => (
                <div key={index}>{component}</div>
              ))}
            {addOnsButton && <>{addOnsButton}</>}
          </div>
        )}
      </TabsList>

      {!hasDirection && (
        <>
          {scrollable ? (
            <ScrollArea className="aero:h-[calc(100vh-280px)]">
              {tabContent()}
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          ) : (
            <>{tabContent()}</>
          )}
        </>
      )}
    </Tabs>
  );
}

export default TabsComponent;
